import React, { useEffect, useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { toast } from '../components/ui/use-toast';
import { Plus, Pencil, Power, Trash2, Search, Globe, DollarSign, Languages as LanguagesIcon, Building2, Ruler, Beaker, Tag, Sprout, RefreshCw, BookOpen, ChefHat, Brush, Soup, Package, LayoutList, Coffee, Milk, Flame, Wine, Martini, Image as ImageIcon, Upload, X } from 'lucide-react';
import { confirm } from '../components/ui/confirm-dialog';
import { mastersService, dishMastersService } from '../services/mastersService';
import { useT } from '../context/LanguageContext';
import { useModuleAccess } from '../utils/useModuleAccess';
import AccessDenied from '../components/AccessDenied';
import { useRestaurant } from '../context/RestaurantContext';
import { useToast } from '../components/ui/use-toast';
import apiClient from '../services/api';
// Ingredient CRUD goes through the unified dish-master controller; allergens are no longer tied to ingredients.
import { Textarea } from '../components/ui/textarea';

const TABS = (t) => [
  {
    key: 'countries',
    group: 'General',
    title: t('masters.tab.countries'),
    singular: t('masters.singular.country'),
    icon: Globe,
    columns: [
      { label: t('masters.column.code'), field: 'code', input: { required: true, maxLength: 3 } },
      { label: t('masters.column.name'), field: 'name', input: { required: true } },
      { label: t('masters.column.dialCode'), field: 'dial_code', input: { placeholder: '+230' } },
    ],
  },
  {
    key: 'currencies',
    group: 'General',
    title: t('masters.tab.currencies'),
    singular: t('masters.singular.currency'),
    icon: DollarSign,
    columns: [
      { label: t('masters.column.code'), field: 'code', input: { required: true, maxLength: 3 } },
      { label: t('masters.column.name'), field: 'name', input: { required: true } },
      { label: t('masters.column.symbol'), field: 'symbol', input: { placeholder: 'Rs' } },
    ],
  },
  {
    key: 'languages',
    group: 'General',
    title: t('masters.tab.languages'),
    singular: t('masters.singular.language'),
    icon: LanguagesIcon,
    columns: [
      { label: t('masters.column.code'), field: 'code', input: { required: true, maxLength: 10 } },
      { label: t('masters.column.name'), field: 'name', input: { required: true } },
      { label: t('masters.column.nativeName'), field: 'native_name' },
    ],
  },
  {
    key: 'companyTypes',
    group: 'General',
    title: 'Company Types',
    singular: 'Company Type',
    icon: Building2,
    noDeactivate: true,
    columns: [
      { label: t('masters.column.code'), field: 'code', input: { required: true, maxLength: 30 } },
      { label: t('masters.column.name'), field: 'name', input: { required: true } },
      { label: t('masters.column.description'), field: 'description' },
    ],
  },
  {
    key: 'sectionTypes',
    group: 'General',
    title: 'Section Types',
    singular: 'Section Type',
    icon: Building2,
    columns: [
      { label: t('masters.column.code'), field: 'code', input: { required: true, maxLength: 30 } },
      { label: t('masters.column.name'), field: 'name', input: { required: true } },
      { label: t('masters.column.description'), field: 'description' },
    ],
  },
  // Menu Masters all route through the unified dish-master controller's list and detail paths.
  {
    key: 'dishCategories',
    group: 'Menu Masters',
    title: 'Categories',
    singular: 'Category',
    icon: BookOpen,
    useDishMasters: true,
    masterType: 'category',
    columns: [
      { label: 'Image', field: 'image', kind: 'image' },
      { label: 'Name', field: 'name', input: { required: true } },
      { label: 'Description', field: 'description' },
    ],
  },
  {
    key: 'menuSections',
    group: 'Menu Masters',
    title: 'Sections',
    singular: 'Section',
    icon: LayoutList,
    useDishMasters: true,
    masterType: 'section',
    columns: [
      { label: 'Name', field: 'name', input: { required: true, placeholder: 'e.g. Starters, Main Course' } },
      { label: 'Description', field: 'description' },
    ],
  },
  {
    key: 'dishSubCategories',
    group: 'Menu Masters',
    title: 'Sub Categories',
    singular: 'Sub Category',
    icon: BookOpen,
    useDishMasters: true,
    masterType: 'sub_category',
    columns: [
      { label: 'Image', field: 'image', kind: 'image' },
      { label: 'Name', field: 'name', input: { required: true, placeholder: 'e.g. Creole Curry' } },
      {
        label: 'Category',
        field: 'category_id',
        kind: 'select',
        optionsMasterType: 'category',
        placeholder: '— All categories —',
        // The list view shows category_name, which the server emits; the edit dialog binds category_id.
        displayField: 'category_name',
      },
      { label: 'Description', field: 'description' },
    ],
  },
  {
    key: 'dishTags',
    group: 'Menu Masters',
    title: 'Tags',
    singular: 'Tag',
    icon: Tag,
    useDishMasters: true,
    masterType: 'tag',
    columns: [
      { label: 'Name', field: 'name', input: { required: true } },
      { label: 'Description', field: 'description' },
    ],
  },
  {
    key: 'ingredients',
    group: 'Menu Masters',
    title: 'Ingredients',
    singular: 'Ingredient',
    icon: Sprout,
    custom: 'ingredients',
  },
  /* Live counters are register entries like ingredients: a counter and the
     ingredients it cooks through, defined once and offered to every buffet. */
  {
    key: 'liveCounters',
    group: 'Menu Masters',
    title: 'Live Counters',
    singular: 'Live Counter',
    icon: Flame,
    custom: 'liveCounters',
  },
  {
    key: 'beverageTypes',
    group: 'Menu Masters',
    title: 'Beverage Types',
    singular: 'Beverage Type',
    icon: Coffee,
    useDishMasters: true,
    masterType: 'beverage_type',
    columns: [
      { label: 'Name', field: 'name', input: { required: true, placeholder: 'e.g. Coffee, Tea, Juice' } },
      { label: 'Offers Milk', field: 'allows_milk', kind: 'bool' },
      { label: 'Description', field: 'description' },
    ],
  },
  {
    key: 'milkTypes',
    group: 'Menu Masters',
    title: 'Milk Types',
    singular: 'Milk Type',
    icon: Milk,
    useDishMasters: true,
    masterType: 'milk_type',
    hardDelete: true,
    columns: [
      { label: 'Name', field: 'name', input: { required: true, placeholder: 'e.g. Whole, Almond, Oat' } },
      { label: 'Description', field: 'description' },
    ],
  },
  {
    key: 'coffeeRoasts',
    group: 'Menu Masters',
    title: 'Coffee Roasts',
    singular: 'Coffee Roast',
    icon: Flame,
    useDishMasters: true,
    masterType: 'coffee_roast',
    hardDelete: true,
    columns: [
      { label: 'Name', field: 'name', input: { required: true, placeholder: 'e.g. Light, Medium, Dark, Espresso' } },
      { label: 'Description', field: 'description' },
    ],
  },
  {
    key: 'dishSizes',
    group: 'Menu Masters',
    title: 'Dish Sizes',
    singular: 'Dish Size',
    icon: Ruler,
    useDishMasters: true,
    masterType: 'size',
    columns: [
      { label: 'Name', field: 'name', input: { required: true } },
      { label: 'Description', field: 'description' },
    ],
  },
  {
    key: 'dishUnits',
    group: 'Menu Masters',
    title: 'Ingredient Units',
    singular: 'Ingredient Unit',
    icon: Beaker,
    useDishMasters: true,
    masterType: 'unit',
    columns: [
      { label: 'Name', field: 'name', input: { required: true } },
      { label: 'Description', field: 'description' },
    ],
  },
  // -- Kitchen Masters ---------------------------------------------------- //
  {
    key: 'cookingStations',
    group: 'Kitchen Masters',
    title: 'Cooking Stations',
    singular: 'Cooking Station',
    icon: ChefHat,
    useDishMasters: true,
    masterType: 'cooking_station',
    hardDelete: true,
    columns: [
      { label: 'Name', field: 'name', input: { required: true, placeholder: 'e.g. Dosa Station, Tandoor' } },
      { label: 'Description', field: 'description' },
    ],
  },
  {
    key: 'platingNotes',
    group: 'Kitchen Masters',
    title: 'Plating Notes',
    singular: 'Plating Note',
    icon: Brush,
    useDishMasters: true,
    masterType: 'plating_note',
    hardDelete: true,
    columns: [
      { label: 'Name', field: 'name', input: { required: true, placeholder: 'e.g. Serve folded' } },
      { label: 'Description', field: 'description' },
    ],
  },
  {
    key: 'servingVessels',
    group: 'Kitchen Masters',
    title: 'Serving Vessels',
    singular: 'Serving Vessel',
    icon: Soup,
    useDishMasters: true,
    masterType: 'serving_vessel',
    hardDelete: true,
    columns: [
      { label: 'Name', field: 'name', input: { required: true, placeholder: 'e.g. Steel Plate' } },
      { label: 'Description', field: 'description' },
    ],
  },
  {
    key: 'parcelCutlery',
    group: 'Kitchen Masters',
    title: 'Cutlery',
    singular: 'Cutlery Item',
    icon: Package,
    useDishMasters: true,
    masterType: 'parcel_cutlery',
    hardDelete: true,
    columns: [
      { label: 'Name', field: 'name', input: { required: true, placeholder: 'e.g. Plastic Spoon' } },
      { label: 'Description', field: 'description' },
    ],
  },
  // Liquor Masters: bar reference catalogues routed through the unified controller and read by the dish form.
  {
    key: 'liquorTypes',
    group: 'Liquor Masters',
    title: 'Liquor Types',
    singular: 'Liquor Type',
    icon: Wine,
    useDishMasters: true,
    masterType: 'liquor_type',
    hardDelete: true,
    columns: [
      { label: 'Name', field: 'name', input: { required: true, placeholder: 'e.g. Spirit, Beer, Wine, Liqueur' } },
      { label: 'Description', field: 'description' },
    ],
  },
  {
    key: 'liquorServingSizes',
    group: 'Liquor Masters',
    title: 'Serving Sizes',
    singular: 'Serving Size',
    icon: Martini,
    useDishMasters: true,
    masterType: 'liquor_serving_size',
    hardDelete: true,
    columns: [
      { label: 'Name', field: 'name', input: { required: true, placeholder: 'e.g. Small Peg, Pint, Full Bottle' } },
      { label: 'Default ml', field: 'default_ml', input: { type: 'number', min: 0, placeholder: '30' }, default: '0' },
      {
        label: 'Applies To',
        field: 'applies_to',
        kind: 'select',
        default: 'any',
        // Static options — mirror LiquorServingSizeMaster.APPLIES_TO_CHOICES.
        options: [
          { value: 'any', label: 'Any' },
          { value: 'spirit', label: 'Spirit' },
          { value: 'beer', label: 'Beer' },
          { value: 'wine', label: 'Wine' },
        ],
      },
      { label: 'Description', field: 'description' },
    ],
  },
];

function MasterPanel({ tab, canEdit = true, canCreate = true }) {
  const { t } = useT();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [editing, setEditing] = useState(null); // null | row object
  const [showAdd, setShowAdd] = useState(false);

  // Menu-master tabs route through the unified dish-master controller; everything else stays on mastersService.
  const useDish = !!tab.useDishMasters;
  const dishType = tab.masterType;

  const load = async () => {
    setLoading(true);
    try {
      const data = useDish
        ? await dishMastersService.refresh(dishType, { includeInactive })
        : await mastersService.list(tab.key, { includeInactive });
      setRows(data);
    } catch (e) {
      const apiMessage = e?.response?.data?.error || e?.response?.data?.detail || e?.message || String(e);
      toast({ title: t('masters.loadFailed'), description: apiMessage });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab.key, includeInactive]);

  const onSave = async (payload, id) => {
    try {
      if (useDish) {
        if (id) await dishMastersService.update(dishType, id, payload);
        else    await dishMastersService.create(dishType, payload);
      } else {
        if (id) await mastersService.update(tab.key, id, payload);
        else    await mastersService.create(tab.key, payload);
      }
      toast({ title: id ? t('masters.updated') : t('masters.added') });
      setEditing(null);
      setShowAdd(false);
      load();
    } catch (e) {
      const msg = e?.response?.data?.error || e?.message || t('common.saveFailed');
      toast({ title: t('common.saveFailed'), description: msg });
    }
  };

  const onToggleActive = async (row) => {
    try {
      if (useDish) {
        if (row.is_active) await dishMastersService.remove(dishType, row.id);
        else               await dishMastersService.update(dishType, row.id, { is_active: true });
      } else {
        if (row.is_active) await mastersService.remove(tab.key, row.id);
        else               await mastersService.update(tab.key, row.id, { is_active: true });
      }
      load();
    } catch (e) {
      toast({ title: t('masters.loadFailed'), description: String(e?.message || e) });
    }
  };

  // Permanent delete, enabled only for tabs flagged hardDelete, via the controller's ?hard=true path.
  const onHardDelete = async (row) => {
    const ok = await confirm({
      title: `Delete ${tab.singular.toLowerCase()}?`,
      description: `“${row.name}” will be permanently removed. This cannot be undone.`,
      confirmText: 'Delete',
      tone: 'destructive',
    });
    if (!ok) return;
    try {
      await dishMastersService.remove(dishType, row.id, { hard: true });
      toast({ title: `${tab.singular} deleted` });
      load();
    } catch (e) {
      const msg = e?.response?.data?.error || e?.message || t('common.saveFailed');
      toast({ title: t('common.saveFailed'), description: msg });
    }
  };

  const filtered = rows.filter((r) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return tab.columns.some((c) => String(r[c.field] || '').toLowerCase().includes(s));
  });

  return (
    <div className="space-y-4 h-full flex flex-col min-h-0">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search size={16} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('masters.searchPlaceholder', { tab: tab.title.toLowerCase() })}
              className="pl-8 w-72"
            />
          </div>
          {!loading && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 whitespace-nowrap">
              {search
                ? `${filtered.length} of ${rows.length}`
                : `${rows.length} ${rows.length === 1 ? tab.singular.toLowerCase() : tab.title.toLowerCase()}`}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <label className="text-xs flex items-center gap-1.5 text-muted-foreground">
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
            />
            {t('common.showInactive')}
          </label>
          <Button
            variant="outline"
            size="icon"
            onClick={load}
            disabled={loading}
            title="Refresh"
            aria-label="Refresh"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </Button>
          {canCreate && (
            <Button onClick={() => setShowAdd(true)} size="icon" title={t('masters.addButton', { item: tab.singular })} aria-label={t('masters.addButton', { item: tab.singular })}>
              <Plus size={16} />
            </Button>
          )}
        </div>
      </div>

      {/* Internal scroll keeps the tabs and search bar put while a long list scrolls under a sticky header. */}
      <div className="border rounded-md overflow-hidden overflow-y-auto flex-1 min-h-0">
        <Table>
          <TableHeader className="bg-card sticky top-0 z-10">
            <TableRow>
              {tab.columns.map((c) => (
                <TableHead key={c.field}>{c.label}</TableHead>
              ))}
              <TableHead className="w-28">{t('common.status')}</TableHead>
              <TableHead className="w-28 text-right">{t('common.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={tab.columns.length + 2} className="text-center py-6 text-muted-foreground">{t('common.loading')}</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={tab.columns.length + 2} className="text-center py-6 text-muted-foreground">{t('common.noEntries')}</TableCell></TableRow>
            ) : filtered.map((row) => (
              <TableRow key={row.id} className={row.is_active ? '' : 'opacity-60'}>
                {tab.columns.map((c) => (
                  <TableCell key={c.field}>
                    {c.kind === 'image'
                      ? (row[c.field]
                        ? <img src={row[c.field]} alt={row.name} className="h-9 w-9 rounded-md object-cover border" />
                        : <div className="h-9 w-9 rounded-md border border-dashed flex items-center justify-center text-slate-300"><ImageIcon size={14} /></div>)
                      : c.kind === 'bool'
                      ? (row[c.field] ? 'Yes' : 'No')
                      : c.options
                        ? (c.options.find((o) => o.value === row[c.field])?.label || row[c.field] || '—')
                        : (row[c.displayField || c.field] || '—')}
                  </TableCell>
                ))}
                <TableCell>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${row.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}`}>
                    {row.is_active ? t('common.active') : t('common.inactive')}
                  </span>
                </TableCell>
                <TableCell className="text-right space-x-1">
                  {canEdit ? (
                    <Button variant="ghost" size="icon" onClick={() => setEditing(row)} title={t('common.edit')}>
                      <Pencil size={14} />
                    </Button>
                  ) : (
                    <span className="text-xs text-slate-400 italic">View only</span>
                  )}
                  {canEdit && !tab.noDeactivate && (
                    <Button variant="ghost" size="icon" onClick={() => onToggleActive(row)} title={row.is_active ? t('common.deactivate') : t('common.activate')}>
                      <Power size={14} />
                    </Button>
                  )}
                  {canEdit && tab.hardDelete && (
                    <Button variant="ghost" size="icon" onClick={() => onHardDelete(row)} title={t('common.delete')} className="text-destructive hover:text-destructive">
                      <Trash2 size={14} />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {(showAdd || editing) && (
        <MasterEditDialog
          tab={tab}
          row={editing}
          open={true}
          onClose={() => { setShowAdd(false); setEditing(null); }}
          onSubmit={onSave}
        />
      )}
    </div>
  );
}

function MasterEditDialog({ tab, row, open, onClose, onSubmit }) {
  const { t } = useT();
  const buildInitial = () => tab.columns.reduce(
    (acc, c) => ({
      ...acc,
      [c.field]: c.kind === 'bool'
        ? !!row?.[c.field]
        // Nullish so a numeric 0 survives, falling back to the column's default for new rows.
        : (row?.[c.field] ?? c.default ?? ''),
    }),
    {},
  );
  const [form, setForm] = useState(buildInitial);
  // Cache of select options keyed by the master_type they came from.
  const [selectOptions, setSelectOptions] = useState({});

  useEffect(() => {
    setForm(buildInitial());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row, tab.key]);

  // Fetch dropdown options for any column that declares `optionsMasterType`.
  useEffect(() => {
    const wanted = tab.columns
      .filter((c) => c.kind === 'select' && c.optionsMasterType)
      .map((c) => c.optionsMasterType);
    const unique = [...new Set(wanted)];
    unique.forEach(async (mt) => {
      try {
        const rows = await dishMastersService.refresh(mt);
        setSelectOptions((prev) => ({ ...prev, [mt]: Array.isArray(rows) ? rows : [] }));
      } catch {
        setSelectOptions((prev) => ({ ...prev, [mt]: [] }));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab.key]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {row
              ? t('masters.editTitle', { item: tab.singular })
              : t('masters.addTitle', { item: tab.singular })}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {tab.columns.map((c) => (
            <div key={c.field} className="space-y-1">
              {c.kind === 'image' ? (
                <>
                  <Label>{c.label}</Label>
                  <div className="flex items-center gap-3">
                    {(() => {
                      const v = form[c.field];
                      const preview = v instanceof File ? form[`_preview_${c.field}`] : v;
                      return preview ? (
                        <img src={preview} alt="" className="h-16 w-16 rounded-md object-cover border" />
                      ) : (
                        <div className="h-16 w-16 rounded-md border border-dashed flex items-center justify-center text-slate-300">
                          <ImageIcon size={20} />
                        </div>
                      );
                    })()}
                    <div className="flex flex-col gap-1.5">
                      <label className="inline-flex items-center gap-1.5 text-sm text-primary cursor-pointer hover:underline">
                        <Upload size={14} />
                        {form[c.field] ? 'Change image' : 'Select image'}
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            setForm({
                              ...form,
                              [c.field]: file,
                              [`_preview_${c.field}`]: URL.createObjectURL(file),
                            });
                            e.target.value = '';
                          }}
                        />
                      </label>
                      {form[c.field] && (
                        <button
                          type="button"
                          className="inline-flex items-center gap-1.5 text-sm text-destructive hover:underline"
                          onClick={() => setForm({ ...form, [c.field]: null, [`_preview_${c.field}`]: null })}
                        >
                          <X size={14} /> Remove image
                        </button>
                      )}
                    </div>
                  </div>
                </>
              ) : c.kind === 'bool' ? (
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    id={c.field}
                    checked={!!form[c.field]}
                    onChange={(e) => setForm({ ...form, [c.field]: e.target.checked })}
                  />
                  {c.label}
                </label>
              ) : (
                <>
              <Label htmlFor={c.field}>{c.label}{c.input?.required ? ' *' : ''}</Label>
              {c.kind === 'select' ? (
                <select
                  id={c.field}
                  value={form[c.field] || ''}
                  onChange={(e) => setForm({ ...form, [c.field]: e.target.value })}
                  className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors"
                >
                  {c.options ? (
                    // Static option list (e.g. Applies To: any/spirit/beer/wine).
                    c.options.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))
                  ) : (
                    <>
                      <option value="">{c.placeholder || '— None —'}</option>
                      {(selectOptions[c.optionsMasterType] || []).map((opt) => (
                        <option key={opt.id} value={opt.id}>{opt.name}</option>
                      ))}
                    </>
                  )}
                </select>
              ) : (
                <Input
                  id={c.field}
                  value={form[c.field]}
                  onChange={(e) => setForm({ ...form, [c.field]: e.target.value })}
                  {...(c.input || {})}
                />
              )}
                </>
              )}
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t('common.cancel')}</Button>
          <Button onClick={() => {
            // Image columns: send the File when picked, remove_image when cleared, and otherwise omit the field.
            const payload = { ...form };
            tab.columns.forEach((c) => {
              if (c.kind !== 'image') return;
              delete payload[`_preview_${c.field}`];
              const v = payload[c.field];
              if (v instanceof File) return;
              delete payload[c.field];
              if (!v && row?.[c.field]) payload.remove_image = true;
            });
            onSubmit(payload, row?.id);
          }}>{row ? t('common.save') : t('common.add')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function MastersManagement() {
  const { t } = useT();
  const tabs = TABS(t);
  const [active, setActive] = useState('countries');
  const activeTab = tabs.find((tab) => tab.key === active) || tabs[0];
  const access = useModuleAccess('master_data');

  if (!access.canView) {
    return <AccessDenied perm="master_data.view" what="Master Data" />;
  }

  return (
    <div className="p-6 h-full flex flex-col">
      <div className="mb-8">
        <h1 className="font-display text-3xl font-semibold text-foreground tracking-tight leading-none">{t('masters.title')}</h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[240px_1fr] gap-6 items-stretch flex-1 min-h-0">
        {/* Vertical rail of master types, grouped */}
        <Card className="p-2">
          <nav className="flex flex-col gap-1">
            {(() => {
              const grouped = [];
              let lastGroup = null;
              tabs.forEach((tab) => {
                if (tab.group !== lastGroup) {
                  grouped.push({ kind: 'header', label: tab.group || 'Other' });
                  lastGroup = tab.group;
                }
                grouped.push({ kind: 'tab', tab });
              });
              return grouped.map((entry, i) => {
                if (entry.kind === 'header') {
                  return (
                    <div key={`h-${i}`} className="px-3 pt-3 pb-1 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                      {entry.label}
                    </div>
                  );
                }
                const Icon = entry.tab.icon;
                const isActive = entry.tab.key === active;
                return (
                  <button
                    key={entry.tab.key}
                    type="button"
                    onClick={() => setActive(entry.tab.key)}
                    aria-pressed={isActive}
                    className={`flex items-center justify-between gap-2 px-3 py-2 rounded-md text-sm transition-colors text-left ${
                      isActive ? 'bg-primary text-primary-foreground' : 'text-foreground hover:bg-accent'
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      <Icon size={16} />
                      {entry.tab.title}
                    </span>
                  </button>
                );
              });
            })()}
          </nav>
        </Card>

        {/* Content panel */}
        <Card className="flex flex-col min-h-0">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <activeTab.icon size={18} /> {activeTab.title}
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-1 min-h-0 flex flex-col">
            {!access.canEdit && (
              <div className="mb-4 rounded-md border border-warning/30 bg-warning-soft px-4 py-2.5 text-sm text-warning">
                <strong>Read-only access.</strong> Your role can view master data but cannot change it.
              </div>
            )}
            {activeTab.custom === 'ingredients' ? (
              <IngredientsPanel canEdit={access.canEdit} canCreate={access.canCreate} />
            ) : activeTab.custom === 'liveCounters' ? (
              <LiveCountersPanel canEdit={access.canEdit} canCreate={access.canCreate} />
            ) : (
              <MasterPanel tab={activeTab} canEdit={access.canEdit} canCreate={access.canCreate} />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// Ingredients Panel - custom because of the allergen multi-select.

function IngredientsPanel({ canEdit = true, canCreate = true }) {
  const { restaurantId } = useRestaurant();
  const { toast } = useToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);

  const load = async () => {
    if (!restaurantId) return;
    setLoading(true);
    try {
      // Ingredients flow through the unified dish-master controller; allergens are tagged on dishes now.
      const ings = await dishMastersService.refresh('ingredient', { restaurantId });
      setRows(ings);
    } catch (e) {
      toast({ title: 'Error', description: e.error || 'Failed to load', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId]);

  const openCreate = () => setEditing({ name: '', description: '', is_active: true });
  const openEdit = (ing) => setEditing({
    id: ing.id,
    name: ing.name,
    description: ing.description || '',
    is_active: ing.is_active,
  });

  const handleSave = async () => {
    if (!editing.name?.trim()) {
      toast({ title: 'Name required', variant: 'destructive' });
      return;
    }
    try {
      const payload = {
        restaurant_id: restaurantId,
        name: editing.name.trim(),
        description: editing.description || '',
        is_active: editing.is_active,
      };
      if (editing.id) await dishMastersService.update('ingredient', editing.id, payload);
      else            await dishMastersService.create('ingredient', payload);
      toast({ title: 'Saved' });
      setEditing(null);
      load();
    } catch (e) {
      const msg = e?.response?.data?.error || e?.message || 'Failed to save';
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    }
  };

  const handleToggleActive = async (ing) => {
    try {
      if (ing.is_active) {
        await dishMastersService.remove('ingredient', ing.id);
      } else {
        await dishMastersService.update('ingredient', ing.id, { is_active: true });
      }
      load();
    } catch (e) {
      const msg = e?.response?.data?.error || e?.message || 'Failed';
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    }
  };

  const filtered = rows.filter((r) =>
    !search || r.name.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div className="space-y-4 h-full flex flex-col min-h-0">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="relative">
          <Search size={16} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search ingredients..." className="pl-8 w-72" />
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={load}
            disabled={loading}
            title="Refresh"
            aria-label="Refresh"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </Button>
          {canCreate && (
            <Button onClick={openCreate} size="icon" title="Add Ingredient" aria-label="Add Ingredient">
              <Plus size={16} />
            </Button>
          )}
        </div>
      </div>

      <div className="border rounded-md overflow-hidden overflow-y-auto flex-1 min-h-0">
        <Table>
          <TableHeader className="bg-card sticky top-0 z-10">
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Description</TableHead>
              <TableHead className="w-24">Status</TableHead>
              <TableHead className="w-28 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={4} className="text-center py-6 text-muted-foreground">Loading…</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={4} className="text-center py-6 text-muted-foreground">No ingredients yet.</TableCell></TableRow>
            ) : filtered.map((row) => (
              <TableRow key={row.id} className={row.is_active ? '' : 'opacity-60'}>
                <TableCell className="font-medium">{row.name}</TableCell>
                <TableCell className="text-sm text-slate-600 max-w-md truncate">{row.description || '—'}</TableCell>
                <TableCell>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${row.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}`}>
                    {row.is_active ? 'Active' : 'Inactive'}
                  </span>
                </TableCell>
                <TableCell className="text-right space-x-1">
                  {canEdit && (
                    <>
                      <Button variant="ghost" size="icon" onClick={() => openEdit(row)} title="Edit">
                        <Pencil size={14} />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleToggleActive(row)} title={row.is_active ? 'Deactivate' : 'Activate'}>
                        <Power size={14} />
                      </Button>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={editing !== null} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing?.id ? 'Edit Ingredient' : 'New Ingredient'}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="e.g., Milk" />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea rows={2} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={handleSave}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


/* Every ingredient a dish cooks with, deduped in first-seen order — the
   dish's OWN ticked ingredients first (the dish form's "Select ingredients"),
   then anything more its variant recipes name. This is the list the counter
   dialog offers once its dish is picked. */
export function dishRecipeIngredientIds(dish) {
  const seen = new Set();
  const out = [];
  const add = (raw) => {
    const id = Number(raw);
    if (id && !seen.has(id)) { seen.add(id); out.push(id); }
  };
  (dish?.ingredients || []).forEach((i) => add(i?.id));
  (dish?.variants || []).forEach((v) => (v?.recipe || []).forEach((r) => add(r?.ingredient_id)));
  return out;
}

/* Live Counters Panel — custom because a counter's whole point is its
   ingredient membership, which the generic panel cannot express. The register
   is what the buffet studio's chips read: define a counter once here — pick
   the DISH it cooks, tick from that dish's recipe ingredients, give each its
   per-100 quantity — and every buffet picks the counter whole.
   Exported: the SAME panel is also the Buffet → Live Counters page, so the
   register has one implementation however it is reached. */
export function LiveCountersPanel({ canEdit = true, canCreate = true }) {
  const { restaurantId } = useRestaurant();
  const { toast } = useToast();
  const [rows, setRows] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [dishes, setDishes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  /* The "+" inside the dialog's ingredient picker: a missing ingredient is
     created in the register on the spot and comes back already ticked. */
  const [ingDraft, setIngDraft] = useState(null);
  const saveIngDraft = async () => {
    if (!ingDraft || !ingDraft.name.trim()) return;
    setIngDraft((d) => d && { ...d, saving: true, error: '' });
    try {
      const row = await dishMastersService.create('ingredient', {
        restaurant_id: restaurantId, name: ingDraft.name.trim(),
      });
      if (!row?.id) throw new Error('Could not create the ingredient');
      setIngredients((v) => [...v, row]);
      setEditing((d) => d && { ...d, ingredientIds: [...d.ingredientIds, row.id] });
      setIngDraft(null);
    } catch (e) {
      setIngDraft((d) => d && {
        ...d, saving: false, error: e?.response?.data?.error || e?.message || 'Failed',
      });
    }
  };

  const load = async () => {
    if (!restaurantId) return;
    setLoading(true);
    try {
      const [{ data }, ings, dishRes] = await Promise.all([
        apiClient.get('/users/live-counters/', {
          params: { restaurant_id: restaurantId, include_inactive: 'true' },
        }),
        // The register still resolves ids to names; WHICH ids are offered comes from the picked dish's recipe.
        dishMastersService.refresh('ingredient', { restaurantId }).catch(() => []),
        // The full dish list, variants and recipes included — the dialog's dish picker reads it.
        apiClient.get(`/users/restaurant/${restaurantId}/dishes/`, {
          params: { page_size: 1000 },
        }).catch(() => null),
      ]);
      setRows(data?.success ? (data.data || []) : []);
      setIngredients(ings || []);
      const dp = dishRes?.data?.success ? dishRes.data.data : [];
      setDishes(Array.isArray(dp) ? dp : (dp?.items || []));
    } catch (e) {
      toast({ title: 'Error', description: e?.message || 'Failed to load', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId]);

  const openCreate = () => {
    setIngDraft(null);
    setEditing({ name: '', description: '', dishId: null, ingredientIds: [], savedIds: [], recipe: {} });
  };
  const openEdit = (c) => {
    setIngDraft(null);
    /* The RECIPE rides on the rows the API sends (qty_per_100 / recipe_unit),
       rebuilt here into the {ingredientId: {qty, unit}} map the save sends. */
    const recipe = {};
    (c.ingredients || []).forEach((i) => {
      if (Number(i.qty_per_100) > 0) {
        recipe[i.id] = { qty: Number(i.qty_per_100), unit: i.recipe_unit || 'kg' };
      }
    });
    setEditing({
      id: c.id,
      name: c.name,
      description: c.description || '',
      dishId: c.dish_id || null,
      ingredientIds: (c.ingredients || []).map((i) => i.id),
      /* Members already saved on the counter: they stay offered (and ticked)
         even if the dish's recipe moved on since, so an edit never silently
         drops what an older save chose. */
      savedIds: (c.ingredients || []).map((i) => i.id),
      recipe,
    });
  };

  const handleSave = async () => {
    if (!editing.name?.trim()) {
      toast({ title: 'Name required', variant: 'destructive' });
      return;
    }
    /* Per-100 amounts are MANDATORY: a ticked ingredient without one cannot
       derive its quantity on any buffet, so the save names it instead. */
    const missing = (editing.ingredientIds || [])
      .filter((id) => !(Number(editing.recipe?.[id]?.qty) > 0));
    if (missing.length) {
      const names = missing.map((id) => (ingredients.find((x) => x.id === id)
        || (dlgDish?.ingredients || []).find((x) => x.id === id) || {}).name || 'an ingredient');
      toast({
        title: 'Recipe amounts required',
        description: `Give the per-100 amount for ${names.join(', ')} — or untick what the counter doesn't use.`,
        variant: 'destructive',
      });
      return;
    }
    try {
      const payload = {
        restaurant_id: restaurantId,
        name: editing.name.trim(),
        description: editing.description || '',
        dish_id: editing.dishId || null,
        ingredient_ids: editing.ingredientIds,
        recipe: editing.recipe || {},
      };
      if (editing.id) await apiClient.put(`/users/live-counters/${editing.id}/`, payload);
      else await apiClient.post('/users/live-counters/', payload);
      toast({ title: 'Saved' });
      setEditing(null);
      load();
    } catch (e) {
      const msg = e?.response?.data?.error || e?.message || 'Failed to save';
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    }
  };

  const handleToggleActive = async (c) => {
    try {
      if (c.is_active) await apiClient.delete(`/users/live-counters/${c.id}/`);
      else await apiClient.put(`/users/live-counters/${c.id}/`, { is_active: true });
      load();
    } catch (e) {
      const msg = e?.response?.data?.error || e?.message || 'Failed';
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    }
  };

  const filtered = rows.filter((r) => !search
    || r.name.toLowerCase().includes(search.toLowerCase())
    || (r.dish_name || '').toLowerCase().includes(search.toLowerCase())
    || (r.ingredients || []).some((i) => i.name.toLowerCase().includes(search.toLowerCase())));

  /* What the dialog offers to tick: the picked dish's recipe ingredients,
     plus anything already ticked (saved members survive a dish edit; a fresh
     "+ New ingredient" lands here too). Order: dish's first, extras after. */
  const dlgDish = editing ? (dishes || []).find((d) => d.id === editing.dishId) : null;
  const dlgOffered = (() => {
    if (!editing) return [];
    const base = dishRecipeIngredientIds(dlgDish);
    const have = new Set(base);
    (editing.ingredientIds || []).forEach((x) => {
      if (!have.has(x)) { have.add(x); base.push(x); }
    });
    return base;
  })();
  const dishOptions = [...(dishes || [])]
    .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));

  return (
    <div className="space-y-4 h-full flex flex-col min-h-0">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="relative">
          <Search size={16} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search counters or their ingredients..." className="pl-8 w-72" />
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={load} disabled={loading}
            title="Refresh" aria-label="Refresh">
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </Button>
          {canCreate && (
            <Button onClick={openCreate} size="icon" title="Add Live Counter" aria-label="Add Live Counter">
              <Plus size={16} />
            </Button>
          )}
        </div>
      </div>

      <div className="border rounded-md overflow-hidden overflow-y-auto flex-1 min-h-0">
        <Table>
          <TableHeader className="bg-card sticky top-0 z-10">
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Ingredients</TableHead>
              <TableHead>Description</TableHead>
              <TableHead className="w-24">Status</TableHead>
              <TableHead className="w-28 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5} className="text-center py-6 text-muted-foreground">Loading…</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center py-6 text-muted-foreground">
                No live counters yet — a tandoor, a dosa griddle, a pasta bar. Define one and every buffet can pick it.
              </TableCell></TableRow>
            ) : filtered.map((row) => (
              <TableRow key={row.id} className={row.is_active ? '' : 'opacity-60'}>
                <TableCell className="font-medium">
                  {row.name}
                  {row.dish_name && (
                    <div className="text-xs font-normal text-muted-foreground">cooks {row.dish_name}</div>
                  )}
                </TableCell>
                <TableCell>
                  {(row.ingredients || []).length === 0 ? (
                    <span className="text-sm text-muted-foreground">—</span>
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {row.ingredients.map((i) => (
                        <span key={i.id} className="rounded-full bg-secondary px-2 py-0.5 text-xs text-foreground"
                          title={Number(i.qty_per_100) > 0
                            ? `${i.qty_per_100} ${i.recipe_unit || 'kg'} of ${i.name} per 100 of ${row.name}`
                            : `No ratio recorded — buffets type this ingredient's quantity by hand`}>
                          {i.name}
                          {Number(i.qty_per_100) > 0 && (
                            <span className="ml-1 text-muted-foreground">
                              · {i.qty_per_100} {i.recipe_unit || 'kg'}/100
                            </span>
                          )}
                        </span>
                      ))}
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-sm text-slate-600 max-w-xs truncate">{row.description || '—'}</TableCell>
                <TableCell>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${row.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}`}>
                    {row.is_active ? 'Active' : 'Inactive'}
                  </span>
                </TableCell>
                <TableCell className="text-right space-x-1">
                  {canEdit && (
                    <>
                      <Button variant="ghost" size="icon" onClick={() => openEdit(row)} title="Edit">
                        <Pencil size={14} />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleToggleActive(row)}
                        title={row.is_active ? 'Deactivate' : 'Activate'}>
                        <Power size={14} />
                      </Button>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={editing !== null} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing?.id ? 'Edit Live Counter' : 'New Live Counter'}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              {/* Dish FIRST: the counter IS a dish cooked live — picking it
                  names the counter and brings that dish's own ingredients in,
                  every one already ticked. */}
              <div className="space-y-2">
                <Label>Dish it cooks *</Label>
                <select value={editing.dishId ?? ''}
                  onChange={(e) => {
                    const dishId = e.target.value ? Number(e.target.value) : null;
                    const next = (dishes || []).find((d) => d.id === dishId);
                    const offered = dishRecipeIngredientIds(next);
                    setEditing((d) => {
                      /* All the dish's ingredients land ticked (untick to
                         drop one); saved members always survive a switch. */
                      const keep = [...offered];
                      (d.savedIds || []).forEach((x) => { if (!keep.includes(x)) keep.push(x); });
                      const recipe = {};
                      keep.forEach((x) => { if (d.recipe?.[x]) recipe[x] = d.recipe[x]; });
                      /* The counter is NAMED by its dish; a hand-typed name is
                         left alone, blank or previous-dish names follow along. */
                      const prev = (dishes || []).find((x) => x.id === d.dishId);
                      const untouched = !d.name.trim() || (prev && d.name.trim() === prev.name);
                      return {
                        ...d, dishId,
                        name: next && untouched ? next.name : d.name,
                        ingredientIds: keep, recipe,
                      };
                    });
                  }}
                  className="h-9 w-full rounded-md border border-border bg-background px-2 text-sm">
                  <option value="">— pick the dish this counter cooks —</option>
                  {dishOptions.map((dsh) => (
                    <option key={dsh.id} value={dsh.id}>{dsh.name}</option>
                  ))}
                </select>
                {dishes !== null && editing.dishId
                  && !(dishes || []).some((d) => d.id === editing.dishId) && (
                  <p className="text-xs text-amber-600">
                    The dish this counter pointed at is gone — its saved ingredients are kept below.
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  placeholder="Same as the dish — filled when you pick it" />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea rows={2} value={editing.description}
                  onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Ingredients it uses — from the dish</Label>
                <div className="max-h-48 overflow-y-auto rounded-md border border-border p-2">
                  <div className="flex flex-wrap items-center gap-1.5">
                    {dlgOffered.length === 0 && (
                      <span className="text-xs text-muted-foreground">
                        {editing.dishId
                          ? 'This dish has no ingredients ticked yet — add them on the dish’s Ingredients tab, or create one here.'
                          : 'Pick the dish first — its ingredients appear here, already ticked.'}
                      </span>
                    )}
                    {dlgOffered.map((id) => {
                      /* The register resolves the name; a row the register
                         list misses still shows via the dish's own copy. */
                      const ing = ingredients.find((x) => x.id === id)
                        || (dlgDish?.ingredients || []).find((x) => x.id === id);
                      if (!ing) return null;
                      const on = editing.ingredientIds.includes(id);
                      return (
                        <button key={id} type="button" aria-pressed={on}
                          onClick={() => setEditing((d) => ({
                            ...d,
                            ingredientIds: on
                              ? d.ingredientIds.filter((x) => x !== id)
                              : [...d.ingredientIds, id],
                          }))}
                          className={`rounded-full border px-2.5 py-1 text-xs font-medium transition ${
                            on ? 'border-primary bg-primary text-primary-foreground'
                              : 'border-border bg-background text-muted-foreground hover:bg-muted'}`}>
                          {ing.name}
                        </button>
                      );
                    })}
                    {/* A missing ingredient is created here, in the same register the
                        Ingredients tab manages, and lands already ticked. */}
                    {ingDraft ? (
                      <span className="inline-flex items-center gap-1.5">
                        <Input value={ingDraft.name} placeholder="Ingredient name" autoFocus
                          onChange={(e) => setIngDraft((d) => d && { ...d, name: e.target.value })}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') { e.preventDefault(); saveIngDraft(); }
                            if (e.key === 'Escape') setIngDraft(null);
                          }}
                          className="h-7 w-44 text-xs" />
                        <Button type="button" size="sm" className="h-7 px-2 text-xs"
                          onClick={saveIngDraft}
                          disabled={ingDraft.saving || !ingDraft.name.trim()}>
                          {ingDraft.saving ? 'Adding…' : 'Add'}
                        </Button>
                        <button type="button" onClick={() => setIngDraft(null)}
                          className="text-muted-foreground hover:text-foreground"
                          aria-label="Cancel new ingredient">
                          <X size={14} />
                        </button>
                      </span>
                    ) : (
                      <button type="button"
                        onClick={() => setIngDraft({ name: '', saving: false, error: '' })}
                        className="inline-flex items-center gap-1 rounded-full border border-dashed border-border px-2.5 py-1 text-xs font-medium text-muted-foreground hover:border-primary/40 hover:text-foreground"
                        title="Create a new ingredient in the register and tick it here">
                        <Plus size={12} /> New ingredient
                      </button>
                    )}
                  </div>
                </div>
                {ingDraft?.error && (
                  <p className="text-xs text-red-600">{ingDraft.error}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  Only the dish&apos;s own ingredients are offered.
                  Buffets pick the counter whole.
                </p>
              </div>
              {/* The counter's RECIPE — a ratio, not a plan: what 100 kg (or 100 pcs)
                  of the finished output takes of each ingredient. The buffet studio
                  plans the counter like a dish and scales these figures by its own
                  prep quantity, so nobody retypes ingredient amounts per buffet. */}
              {editing.ingredientIds.length > 0 && (
                <div className="space-y-2">
                  <Label>Recipe — per 100 kg of {editing.name.trim() || 'this counter'} *</Label>
                  <div className="max-h-44 space-y-1.5 overflow-y-auto rounded-md border border-border p-2">
                    {editing.ingredientIds.map((id) => {
                      const ing = ingredients.find((x) => x.id === id)
                        || (dlgDish?.ingredients || []).find((x) => x.id === id);
                      if (!ing) return null;
                      const spec = editing.recipe?.[id] || {};
                      return (
                        <div key={id} className="flex items-center gap-2 text-sm">
                          <span className="w-40 truncate">{ing.name}</span>
                          <Input type="number" min="0" step="0.5"
                            value={spec.qty ?? ''} placeholder="—"
                            onChange={(e) => setEditing((d) => ({
                              ...d,
                              recipe: {
                                ...d.recipe,
                                [id]: { unit: spec.unit || 'kg', ...d.recipe?.[id],
                                  qty: e.target.value === '' ? '' : Number(e.target.value) },
                              },
                            }))}
                            className="h-8 w-24 text-right tabular-nums" />
                          <select value={spec.unit || 'kg'}
                            onChange={(e) => setEditing((d) => ({
                              ...d,
                              recipe: { ...d.recipe, [id]: { ...d.recipe?.[id], unit: e.target.value } },
                            }))}
                            className="h-8 rounded-md border border-border bg-background px-1.5 text-xs text-muted-foreground">
                            {/* Weighed or counted only — no litres on a counter's recipe. */}
                            <option value="kg">kg</option>
                            <option value="pcs">pcs</option>
                          </select>
                          <span className="text-xs text-muted-foreground">per 100</span>
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Example: 100 kg of Pasta takes 15 kg raw pasta and 20 kg white sauce.
                    A buffet prepping 40 kg then derives 6 kg and 8 kg on its own.
                    <strong className="text-foreground"> Every ticked ingredient needs its
                    amount</strong> — the counter won&apos;t save without them.
                  </p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={handleSave}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
